import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import streamApi from '../../services/streamApi';
import { authApi } from '../../services/authApi';

const AdminContext = createContext(null);

const EMPTY_REVENUE = { total_spades: 0, total_usd: 0, transaction_count: 0 };

export function AdminProvider({ children }) {
  const [pendingMedia, setPendingMedia] = useState([]);
  const [platformFees, setPlatformFees] = useState([]);
  const [revenueSummary, setRevenueSummary] = useState(EMPTY_REVENUE);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [pendingError, setPendingError] = useState('');
  const [revenueError, setRevenueError] = useState('');
  const [usersError, setUsersError] = useState('');
  const [notice, setNotice] = useState('');

  const loadDashboard = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }

    setPendingError('');
    setRevenueError('');
    setUsersError('');

    const [pendingResult, revenueResult, usersResult] = await Promise.allSettled([
      streamApi.getAdminPendingMedia(),
      authApi.getAdminPlatformRevenue(),
      authApi.getAdminUsers(),
    ]);

    let denied = false;

    if (pendingResult.status === 'fulfilled') {
      setPendingMedia(Array.isArray(pendingResult.value) ? pendingResult.value : []);
    } else if (streamApi.isAdminAccessError(pendingResult.reason)) {
      denied = true;
      setPendingMedia([]);
    } else {
      setPendingMedia([]);
      setPendingError(pendingResult.reason?.message || 'Failed to load moderation queue.');
    }

    if (revenueResult.status === 'fulfilled') {
      setRevenueSummary(revenueResult.value?.summary || EMPTY_REVENUE);
      setPlatformFees(Array.isArray(revenueResult.value?.items) ? revenueResult.value.items : []);
    } else if (streamApi.isAdminAccessError(revenueResult.reason)) {
      denied = true;
      setRevenueSummary(EMPTY_REVENUE);
      setPlatformFees([]);
    } else {
      setRevenueSummary(EMPTY_REVENUE);
      setPlatformFees([]);
      setRevenueError(revenueResult.reason?.message || 'Failed to load platform revenue.');
    }

    if (usersResult.status === 'fulfilled') {
      setUsers(Array.isArray(usersResult.value) ? usersResult.value : []);
    } else if (streamApi.isAdminAccessError(usersResult.reason)) {
      denied = true;
      setUsers([]);
    } else {
      setUsers([]);
      setUsersError(usersResult.reason?.message || 'Failed to load platform users.');
    }

    setAccessDenied(denied);

    if (!silent) {
      setLoading(false);
    }
    setRefreshing(false);
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const removePendingMedia = useCallback((mediaId) => {
    setPendingMedia((current) => current.filter((item) => item.id !== mediaId));
  }, []);

  const updateUserBalance = useCallback((userId, spadeBalance) => {
    setUsers((current) => current.map((item) => (
      item.id === userId ? { ...item, spade_balance: spadeBalance } : item
    )));
  }, []);

  const value = useMemo(() => ({
    pendingMedia,
    platformFees,
    revenueSummary,
    users,
    loading,
    refreshing,
    accessDenied,
    pendingError,
    revenueError,
    usersError,
    notice,
    setNotice,
    setAccessDenied,
    setPendingError,
    setUsersError,
    loadDashboard,
    removePendingMedia,
    updateUserBalance,
  }), [
    pendingMedia,
    platformFees,
    revenueSummary,
    users,
    loading,
    refreshing,
    accessDenied,
    pendingError,
    revenueError,
    usersError,
    notice,
    loadDashboard,
    removePendingMedia,
    updateUserBalance,
  ]);

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
}

export function useAdmin() {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within AdminProvider');
  }
  return context;
}
